import { POST_MESSAGE_TYPES } from '@/@types/post-message.types';

import { postMessageBus } from './post-message';

type CopyToClipboardShareData = {
  title?: string;
  text?: string;
};

export type CopyToClipboardResult =
  | 'native-share'
  | 'clipboard'
  | 'exec-command'
  | 'extension'
  | false;

/**
 * Last-resort clipboard copy via the browser extension's postMessage bridge.
 * Sends a `copy-to-clipboard` request to the parent frame and waits for
 * a `copy-to-clipboard-response` back within 5 seconds.
 */
const CLIPBOARD_BRIDGE_TIMEOUT_MS = 5000;

const copyToClipboardViaExtension = async (text: string): Promise<boolean> => {
  // Only works when embedded inside the extension (parent !== self)
  if (typeof window === 'undefined' || window.parent === window) return false;

  const requestId = `copy-${Date.now()}-${Math.random()}`;

  const reply = await postMessageBus.request({
    type: POST_MESSAGE_TYPES.COPY_TO_CLIPBOARD,
    responseType: POST_MESSAGE_TYPES.COPY_TO_CLIPBOARD_RESPONSE,
    message: { text, requestId },
    timeoutMs: CLIPBOARD_BRIDGE_TIMEOUT_MS,
    matches: (response) => response.message?.requestId === requestId,
  });

  return Boolean(reply?.message?.success);
};

export const copyToClipboard = async (
  value: string,
  shareData?: CopyToClipboardShareData,
): Promise<CopyToClipboardResult> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    console.error('Copy/share failed: browser environment is not available');
    return false;
  }

  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const canUseNativeShare = typeof navigator.share === 'function';

  // 1) Use native share API on mobile devices if available
  if (isMobile && canUseNativeShare) {
    try {
      await navigator.share({
        title: shareData?.title,
        text: shareData?.text,
        url: value,
      });
      return 'native-share';
    } catch (error) {
      console.warn(
        'Native share failed, falling back to clipboard methods:',
        error,
      );
    }
  }

  // 2) Fallback to clipboard for desktop or if native share fails
  if (
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    try {
      await navigator.clipboard.writeText(value);
      return 'clipboard';
    } catch (error) {
      console.warn('Clipboard API failed, falling back to execCommand:', error);
    }
  }

  // 3) Fallback method using document.execCommand (deprecated but more compatible)
  if (typeof document !== 'undefined') {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const isCopied = document.execCommand('copy');
      document.body.removeChild(textArea);

      if (isCopied) return 'exec-command';
    } catch (error) {
      console.warn('execCommand fallback failed:', error);
    }
  }

  // 4) Last resort: ask the browser extension to copy via postMessage bridge
  try {
    const extensionResult = await copyToClipboardViaExtension(value);
    if (extensionResult) return 'extension';
  } catch (error) {
    console.warn('Extension clipboard fallback failed:', error);
  }

  console.error('Copy/share failed: all available methods were unsuccessful');
  return false;
};
